// V2 data calls (build/handoff/V2-BUILD.md "API contract"; changes in build/handoff/V2-API-CHANGES.md). Where the server
// has not shipped an action yet (400 "unknown action" / 404), each call falls back to a typed stub so the screens work;
// the stub never invents data the server would own, it only keeps the person's own answers on this device.
import { useEffect, useState } from 'react'
import { ApiError, callFn, ensureSession, track } from './api'
import { rememberPlan, type PlanRow } from './intake'
import { f2 } from '../copyFlow2'
import { browse } from './browse'
import { adoptCalendar } from './calendarChoice'

// ---------- 1. profile (first run) ----------
export type Pace = 'one_great_thing' | 'relaxed' | 'packed'
export const PACES: Pace[] = ['one_great_thing', 'relaxed', 'packed']
/** Copy keys for each pace (approved strings from the intake). */
export const PACE_COPY: Record<Pace, { title: string; help: string }> = {
  one_great_thing: { title: 'tap.pace.one', help: 'cue.pace.hint1' },
  relaxed: { title: 'tap.pace.relaxed', help: 'tap.pace.relaxed_help' },
  packed: { title: 'tap.pace.packed', help: 'tap.pace.packed_help' },
}
/** `city`: the city slug home is in; `building`: that city is still being set up (V2-API-CHANGES 3). */
export type Home = { label: string; lat: number; lng: number; zip?: string; city?: string; building?: boolean }
/** calendar (R-24, 10/17): the calendar they use, saved on the profile too (lib/calendarChoice.ts). */
export type FirstRunProfile = { interests: string[]; home: Home | null; pace: Pace[]; first_run_done: boolean; source?: string; calendar?: 'google' | 'ics' }
export type FirstRunPatch = Partial<Omit<FirstRunProfile, 'home'>> & { home?: (Partial<Home> & { label: string }) | null }

const DONE_KEY = 'mg_first_run_done'
const PROFILE_KEY = 'mg_first_run'
const read = (k: string) => { try { return localStorage.getItem(k) } catch { return null } }
const write = (k: string, v: string | null) => {
  try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v) } catch { /* convenience only */ }
}

/** First run is done on this device (set from the server's answer, so a returning person skips it without a wait). */
export const firstRunDoneHere = () => read(DONE_KEY) === '1'
export const cachedProfile = (): FirstRunProfile | null => { try { return JSON.parse(read(PROFILE_KEY) ?? 'null') } catch { return null } }
/** v3 QA (H): the first run moves on without waiting for each save; this keeps the answers here at once. */
export const keepHere = (p: FirstRunProfile) => keep(p)
function keep(p: FirstRunProfile) {
  write(PROFILE_KEY, JSON.stringify(p))
  if (p.first_run_done) write(DONE_KEY, '1')
}

export async function getFirstRun(): Promise<FirstRunProfile> {
  const p = await callFn<FirstRunProfile>('profile', { get: true })
  keep(p)
  void adoptCalendar(p.calendar) // R-24: a new phone after sign-in starts with the calendar they use
  return p
}
export async function setFirstRun(set: FirstRunPatch): Promise<FirstRunProfile> {
  const p = await callFn<FirstRunProfile>('profile', { set })
  keep(p)
  // B-070: no client event here. 'first_run_done' is not in event_types (every send was a 409); the profile function
  // already records 'first_run_saved' with done: true on the server.
  return p
}

// ---------- answers waiting for a session (review H2) ----------
// When the anonymous session cannot start (sign-in rate limit, outage), the first run keeps its answers here and lets
// the person into Browse; the next time a session starts, the answers are saved and the first run counts as done.
const PENDING_KEY = 'mg_first_run_pending'
const SKIP_KEY = 'mg_first_run_skip'
export function keepPending(p: FirstRunPatch) {
  try {
    const cur = JSON.parse(read(PENDING_KEY) ?? '{}') as FirstRunPatch
    write(PENDING_KEY, JSON.stringify({ ...cur, ...p }))
  } catch { write(PENDING_KEY, JSON.stringify(p)) }
}
export const clearPending = () => write(PENDING_KEY, null)
/** Let this browser session into Browse without the first run (it is asked again on the next visit). */
/** Listing first (alvin 10/17, "Show listing first"): a fresh visitor who opens a shared listing link sees it before the
 *  first run; the first run starts on the first tap that needs it (Save, Build around this) and comes back to that listing.
 *  Kept for this browser session only, and only until the first run is done. */
const LISTING_FIRST_KEY = 'mg_listing_first'
export function letInForListing() { try { sessionStorage.setItem(LISTING_FIRST_KEY, '1') } catch { /* this tab only */ } }
export function clearListingFirst() { try { sessionStorage.removeItem(LISTING_FIRST_KEY) } catch { /* this tab only */ } }
export const firstRunOwed = () => { try { return sessionStorage.getItem(LISTING_FIRST_KEY) === '1' && !firstRunDoneHere() } catch { return false } }
/** The first run, set to come back to `back` (the listing link the visitor is on). */
export const firstRunBackTo = (back: string) => `/first-run?next=${encodeURIComponent(back)}`

export function skipFirstRunForNow() { try { sessionStorage.setItem(SKIP_KEY, '1') } catch { /* one visit only */ } }
const skipped = () => { try { return sessionStorage.getItem(SKIP_KEY) === '1' } catch { return false } }

/** Save answers that were waiting for a session. True when the first run is now done on the server. */
export async function flushPending(): Promise<boolean> {
  const raw = read(PENDING_KEY)
  if (!raw) return false
  let p: FirstRunPatch
  try { p = JSON.parse(raw) as FirstRunPatch } catch { clearPending(); return false }
  try {
    await ensureSession()
    const enough = (p.interests ?? []).length >= 3
    const saved = await setFirstRun({ ...p, ...(enough ? { first_run_done: true } : {}) })
    clearPending()
    return saved.first_run_done
  } catch {
    return false
  }
}

/**
 * Does this visitor still owe the first run? A new person gets a session first; when it cannot start, or the profile
 * cannot be read, they go to Browse (null = unknown) instead of a wall (review H2).
 */
export async function needsFirstRun(): Promise<boolean | null> {
  if (firstRunDoneHere()) return false
  if (await flushPending()) return false
  if (skipped()) return null
  try {
    await ensureSession()
    return !(await getFirstRun()).first_run_done
  } catch {
    return null
  }
}

// ---------- 2-3. one draft plan, plan setup, plan list ----------
export type PlanSetup = { where?: string | { label: string; lat: number; lng: number }; radius_mi: number | null; pace: Pace[]; budget: string[]; active: string[]; topics: string[]; notes?: string }
export type PlanListRow = { id: string; title: string | null; city: string | null; start_date?: string | null; end_date?: string | null; item_count: number; is_draft: boolean; updated_at?: string; status?: string; past?: boolean }

/** A home or plan place the server could not use, in the app's own words (V2-API-CHANGES 3). */
export function placeError(e: unknown, typed: string): string {
  const msg = e instanceof Error ? e.message : ''
  if (e instanceof ApiError && e.status === 429) return f2('where.search_failed')
  if (/which city/i.test(msg)) return f2('city.need_city', { place: typed })
  if (/could not find/i.test(msg)) return f2('onb.home_not_found')
  return f2('onb.save_failed')
}

const SETUP_KEY = (id: string) => `mg_plan_setup:${id}`
/** The Create a plan answers last sent for this plan from this device (Widen distance re-sends them, review M5). */
export const localSetup = (id: string): PlanSetup | null => { try { return JSON.parse(read(SETUP_KEY(id)) ?? 'null') } catch { return null } }

/** The person's one draft plan in their home city (the server makes it when there is none). */
export async function getDraft(): Promise<{ id: string; item_count?: number }> {
  const r = await callFn<{ plan: { id: string; item_count?: number } }>('plan', { draft: true })
  rememberPlan(r.plan.id)
  return r.plan
}

/** Create a plan: set the draft's where, pace and tell us more. Never a second plan. Returns where Browse should open. */
export async function setupPlan(setup: PlanSetup): Promise<{ planId: string; params: URLSearchParams }> {
  const r = await callFn<{ plan: { id: string }; picked_url_params?: Record<string, string> }>('plan', { setup })
  const params = new URLSearchParams()
  Object.entries(r.picked_url_params ?? {}).forEach(([k, v]) => params.set(k, String(v)))
  params.set('plan', r.plan.id)
  write(SETUP_KEY(r.plan.id), JSON.stringify(setup))
  track('plan_setup', { radius: setup.radius_mi, pace: setup.pace, topics: setup.topics.length })
  return { planId: r.plan.id, params }
}

/** Every plan, the server's draft first (its `is_draft` is the only word on which plan is the draft). */
export async function listAllPlans(): Promise<PlanListRow[]> {
  const { plans } = await callFn<{ plans: (PlanRow & Partial<PlanListRow>)[] }>('plan', { list: true })
  return plans.map((p) => ({
    id: p.id, title: p.title, city: p.dest?.label ?? p.city ?? null, start_date: p.start_date || null, end_date: p.end_date || null,
    item_count: p.item_count ?? p.items ?? 0, is_draft: p.is_draft ?? false, updated_at: p.updated_at, status: p.status, past: p.past,
  }))
}

// ---------- 4. picked for this plan ----------
/** The top row Browse shows for a plan (`browse {plan}`). Accepts the key the server picks; see V2-API-CHANGES.md. */
export const PICKED_ROW_KEYS = ['for_plan', 'picked']

// ---------- fr4: the home area builds while the first run goes on (build/handoff/V2-API-CHANGES.md §4) ----------
export type AreaStatus = { state: 'ready' | 'building' | 'done' | string; count: number }
/** How many places are near the point and whether the area is still being built; null when the call failed or the city is
 *  not set up yet (say nothing then). `count` = nearby_count_16km, the number to show. */
export async function areaStatus(lat: number, lng: number): Promise<AreaStatus | null> {
  try {
    const r = await callFn<Record<string, unknown>>('area', { status: { lat, lng } })
    const n = Number(r.nearby_count_16km)
    return typeof r.state === 'string' && r.state !== 'not_live' && Number.isFinite(n) ? { state: r.state, count: n } : null
  } catch {
    return null
  }
}
/** A live count of the home area while it builds (every 6 s, at most 3 minutes; stops at ready / done). */
export function useAreaProgress(point: { lat: number | null; lng: number | null } | null): AreaStatus | null {
  const [s, setS] = useState<AreaStatus | null>(null)
  const lat = point?.lat ?? null, lng = point?.lng ?? null
  useEffect(() => {
    if (lat === null || lng === null) { setS(null); return }
    let stop = false, timer = 0
    const started = Date.now()
    const tick = async () => {
      const got = await areaStatus(lat, lng)
      if (stop || !got) return
      // R-60 blind round 1 (3 of 3 testers): the area's place count (map places only) was smaller than a mood's count on
      // Browse (places + events), so a part looked bigger than the whole. Once the area is ready, the line shows Browse's own
      // total around home (the same 16 km and time window the moods count in); while building, the live place count.
      if (got.state !== 'building') {
        try {
          const b = await browse({ point: { lat, lng }, radius_km: 16, limit: 1 })
          if (stop) return
          if (Number.isFinite(b.total) && b.total > 0) got.count = b.total
        } catch { /* keep the place count */ }
      }
      setS(got)
      if (got.state === 'building' && Date.now() - started < 180_000) timer = window.setTimeout(tick, 6000)
    }
    void tick()
    return () => { stop = true; window.clearTimeout(timer) }
  }, [lat, lng])
  return s
}
